"use client";

import { Label } from "@/components/ui/label";
import { useInteractions } from "../../../hooks/interactionsProvider";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UploadFile, { FileType } from "@/components/utils/uploadFile";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { SrclangType, getPlatformLanguages } from "@/db/schema";
import { getLanguageLabel, t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function AudioInteractionContent() {
    const { dir } = useLocale();
    const { user } = useAuth();
    const { selectedInteraction, updateInteraction } = useInteractions();

    const platformLanguages = getPlatformLanguages();
    if (!selectedInteraction || selectedInteraction.type !== "audio")
        return <></>;

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <Label>
                    {t("interaction-modal.audio.content.sentence.label", undefined, undefined)}
                </Label>

                <Input
                    value={selectedInteraction.content.sentence}
                    onChange={(e) =>
                        updateInteraction(selectedInteraction.id, {
                            content: {
                                ...selectedInteraction.content,
                                sentence: e.target.value,
                            },
                        })
                    }
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label>
                    {t("interaction-modal.audio.content.language.label", undefined, undefined)}
                </Label>

                <Select
                    dir={dir}
                    value={
                        (selectedInteraction.content.language ||
                            "en") satisfies SrclangType
                    }
                    onValueChange={(value) =>
                        updateInteraction(selectedInteraction.id, {
                            content: {
                                ...selectedInteraction.content,
                                language: value as SrclangType,
                            },
                        })
                    }
                >
                    <SelectTrigger>
                        <SelectValue
                            placeholder={t(
                                "interaction-modal.audio.content.language.placeholder",
                                undefined,
                                undefined
                            )}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        {platformLanguages.map((language) => (
                            <SelectItem
                                key={language.code}
                                value={language.code}
                            >
                                {getLanguageLabel(language.code)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex flex-col gap-2">
                <Label>
                    {t("interaction-modal.audio.content.recording.label", undefined, undefined)}
                </Label>

                <Tabs dir={dir} defaultValue="upload">
                    <TabsList className="w-full">
                        <TabsTrigger className="w-full" value="upload">
                            {t(
                                "interaction-modal.audio.content.recording.upload",
                                undefined,
                                undefined
                            )}
                        </TabsTrigger>
                        <TabsTrigger className="w-full" value="record">
                            {t(
                                "interaction-modal.audio.content.recording.record",
                                undefined,
                                undefined
                            )}
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="upload">
                        <UploadFile
                            initialFile={
                                selectedInteraction.content.sentenceAudioFile
                                    ? {
                                          fileUrl:
                                              selectedInteraction.content
                                                  .sentenceAudioFile.url,
                                          fileName:
                                              selectedInteraction.content
                                                  .sentenceAudioFile.name,
                                      }
                                    : undefined
                            }
                            options={{
                                allowedFileTypes: [FileType.Audio],
                                fileUploadPath: `users/${
                                    user!.id
                                }/audio-interaction`,
                                maxFileSize: 25,
                                className: "h-24",
                            }}
                            onUploadFinished={(file) => {
                                updateInteraction(selectedInteraction.id, {
                                    content: {
                                        ...selectedInteraction.content,
                                        sentenceAudioFile: {
                                            transcription: undefined,
                                            url: file.fileUrl,
                                            name: file.fileName,
                                        },
                                    },
                                });
                            }}
                        />
                    </TabsContent>
                    <TabsContent value="record">
                        <div className="h-24 border flex w-full justify-center items-center text-sm rounded-md">
                            {t("common.in-development")}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
